import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { resources } from "@constants/hardcoded-strings";
import { MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_DETAILS_URL } from "@constants/routes";
import { ButtonSizeTypes } from "@enums/button-size-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedAxnAttributeLabel } from "@idw-types/data/reference-data/axn";
import { navigateToUtil } from "@utils/navigation-util";
import { injectAxnAttributeLabelId } from "@utils/parameter-injector";
import { sortStrings } from "@utils/string-util";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { TableCellWithButton } from "@containers/common/table-cell-with-button/table-cell-with-button";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import styles from "./attribute-labels-table-container.less";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type AttributeLabelsTableContainerProps = {
    axnAttributeLabels: Array<FormattedAxnAttributeLabel>;
    tableState: TableState;
    isPageHeight: boolean;
};

type AttributeLabelsTableDataRow = FormattedAxnAttributeLabel & {
    attributeTypeId: string;
    attributeTypeName: string;
};

export const AttributeLabelsTableContainer = (
    props: AttributeLabelsTableContainerProps
) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const routerLocation = useLocation();

    const { axnAttributeLabels, tableState, isPageHeight } = props;

    const handleViewOnClick = (attributeLabel: FormattedAxnAttributeLabel) => {
        const url = injectAxnAttributeLabelId(MANAGEMENT_CONSOLE_MASTER_DATA_ATTRIBUTE_LABELS_DETAILS_URL, attributeLabel.id);
        navigateToUtil(url, navigate, routerLocation.pathname);
    };

    const attributeLabelTableData: Array<AttributeLabelsTableDataRow> = useMemo(() => {
        return axnAttributeLabels.map(label => ({
            ...label,
            attributeTypeId: label.attributeType?.id || "",
            attributeTypeName: label.attributeType?.name || ""
        }));
    }, [axnAttributeLabels]);

    const columnHelper = createColumnHelper<AttributeLabelsTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.display({
            id: "action",
            header: "",
            enableSorting: false,
            enableColumnFilter: false,
            cell: (props) => (
                <TableCellWithButton
                    id="view-attribute-label-details-button"
                    size={ButtonSizeTypes.SMALL}
                    variant={"outline-primary"}
                    icon={faEye}
                    text={""}
                    originalObject={props.row.original}
                    onClick={handleViewOnClick}
                />
            )
        }),
        columnHelper.accessor("id", {
            header: resources.tables.attributeLabels.columns.id.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("label", {
            header: resources.tables.attributeLabels.columns.label.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.label, b.original.label)
        }),
        columnHelper.accessor("attributeTypeId", {
            header: resources.tables.attributeLabels.columns.attributeTypeId.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("attributeTypeName", {
            header: resources.tables.attributeLabels.columns.attributeType.header,
            filterFn: "fuzzyText"
        })
    ]), [attributeLabelTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: attributeLabelTableData,
        state: tableState,
        autoResetPageIndex: true,
        sortDescFirst: false,
        filterFns: filterTypes,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.ATTRIBUTE_LABELS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.ATTRIBUTE_LABELS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.ATTRIBUTE_LABELS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.attributeLabelsTableContainer}>
            <IdwTable
                id="attribute-labels"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"attribute-labels"}
                className={styles.attributeLabelsTable}
            />
            <PaginationContainer
                idPrefix="attribute-labels"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};